import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Grid,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
} from '@mui/joy';
import {
  Copy as CopyIcon,
  CheckCircle as CheckIcon,
  HardDrives as DnsIcon,
  ArrowSquareOut as OpenIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface Domain {
  id: string;
  host: string;
  type: 'primary' | 'custom';
  verified: boolean;
  ssl_status: string;
}

interface DNSInstructionsProps {
  open: boolean;
  onClose: () => void;
  domain: Domain;
  siteId: number;
}

interface DNSRecord {
  type: string;
  name: string;
  value: string;
  description: string;
}

interface ProviderInstructions {
  name: string;
  steps: string[];
  screenshot_url?: string;
}

export const DNSInstructions: React.FC<DNSInstructionsProps> = ({
  open,
  onClose,
  domain,
  siteId,
}) => {
  const [selectedProvider, setSelectedProvider] = useState('generic');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Fetch DNS instructions
  const { data: instructionsData, isLoading } = useQuery({
    queryKey: ['dns-instructions', selectedProvider],
    queryFn: async () => {
      const response = await api.get(`/api/cms/dns-instructions/?provider=${selectedProvider}`);
      return response.data || response;
    },
    enabled: open,
  });

  // Fetch domain verification details
  const { data: verificationData } = useQuery({
    queryKey: ['domain-verification', siteId, domain.id],
    queryFn: async () => {
      const response = await api.get(`/api/cms/sites/${siteId}/domains/${domain.id}/verify/`);
      return response.data || response;
    },
    enabled: open && domain.type === 'custom',
  });

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const getDNSRecords = (): DNSRecord[] => {
    // Try to get from the dns-instructions endpoint first
    if (instructionsData) {
      console.log('DNS Instructions - instructionsData:', instructionsData);
      
      // The API returns dns_records which contains the instructions
      const dnsRecords = (instructionsData as any).dns_records;
      if (dnsRecords) {
        console.log('DNS Instructions - found dns_records:', dnsRecords);
        const records: DNSRecord[] = [];
        
        if (dnsRecords.cname) {
          records.push({
            type: dnsRecords.cname.type || 'CNAME',
            name: dnsRecords.cname.name || '@',
            value: dnsRecords.cname.value || '',
            description: dnsRecords.cname.description || 'Point your domain to ApexOS',
          });
        }
        
        if (dnsRecords.www_cname) {
          records.push({
            type: dnsRecords.www_cname.type || 'CNAME',
            name: dnsRecords.www_cname.name || 'www',
            value: dnsRecords.www_cname.value || '',
            description: dnsRecords.www_cname.description || 'Point www subdomain to ApexOS',
          });
        }
        
        // Note: apex_verification is a template, we'll use verification data for actual TXT record
        
        console.log('DNS Instructions - returning records:', records);
        return records;
      }
    }

    // Fall back to verification data if available (includes actual TXT verification token)
    if (!verificationData) return [];
    
    const data = verificationData as any;
    if (!data.dns_instructions) return [];
    
    const { dns_instructions } = data;
    console.log('DNS Instructions - using verificationData:', dns_instructions);
    return [
      {
        type: 'CNAME',
        name: '@',
        value: dns_instructions.cname_record?.value || '',
        description: dns_instructions.cname_record?.description || '',
      },
      {
        type: 'CNAME',
        name: 'www',
        value: dns_instructions.www_cname?.value || '',
        description: dns_instructions.www_cname?.description || '',
      },
      {
        type: 'TXT',
        name: '@',
        value: dns_instructions.verification_txt?.value || '',
        description: dns_instructions.verification_txt?.description || '',
      },
    ];
  };

  const dnsRecords = getDNSRecords();
  const providerInstructions = (instructionsData as any)?.provider || {};

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 800 }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DnsIcon />
            DNS Setup for {domain.host}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert sx={{ mb: 3 }}>
            <Typography level="body-sm">
              Follow these steps to point your domain to ApexOS. DNS changes can take up to 24 hours to propagate.
              {(instructionsData as any)?.platform_managed && (
                <>
                  <br /><br />
                  <strong>Note:</strong> SSL certificates will be automatically provisioned once DNS propagation is complete.
                </>
              )}
            </Typography>
          </Alert>

          {/* Provider Selection */}
          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Your DNS Provider</FormLabel>
            <Select
              value={selectedProvider}
              onChange={(_, value) => setSelectedProvider(value || 'generic')}
            >
              <Option value="generic">Generic DNS Provider</Option>
              <Option value="cloudflare">Cloudflare</Option>
              <Option value="godaddy">GoDaddy</Option>
              <Option value="namecheap">Namecheap</Option>
              <Option value="route53">Amazon Route 53</Option>
            </Select>
          </FormControl>

          {/* Provider-Specific Instructions */}
          {providerInstructions.steps && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  {providerInstructions.name} Setup Steps
                </Typography>
                <List size="sm">
                  {providerInstructions.steps.map((step: string, index: number) => (
                    <ListItem key={index}>
                      <ListItemDecorator>
                        <Chip size="sm" variant="soft">
                          {index + 1}
                        </Chip>
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm">{step}</Typography>
                      </ListItemContent>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* DNS Records */}
          <Typography level="title-md" sx={{ mb: 2 }}>
            DNS Records to Add
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {dnsRecords.map((record, index) => (
              <Grid xs={12} md={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip
                        color={record.type === 'TXT' ? 'warning' : 'primary'}
                        variant="soft"
                        startDecorator={<DnsIcon />}
                      >
                        {record.type}
                      </Chip>
                      <Button
                        size="sm"
                        variant="plain"
                        startDecorator={copiedField === ('value-' + index) ? <CheckIcon /> : <CopyIcon />}
                        onClick={() => copyToClipboard(record.value, 'value-' + index)}
                      >
                        {copiedField === ('value-' + index) ? 'Copied!' : 'Copy'}
                      </Button>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        Name
                      </Typography>
                      <Input
                        value={record.name}
                        readOnly
                        size="sm"
                        endDecorator={
                          <Button
                            size="sm"
                            variant="plain"
                            onClick={() => copyToClipboard(record.name, 'name-' + index)}
                          >
                            <CopyIcon />
                          </Button>
                        }
                      />
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        Value
                      </Typography>
                      <Input
                        value={record.value}
                        readOnly
                        size="sm"
                        endDecorator={
                          <Button
                            size="sm"
                            variant="plain"
                            onClick={() => copyToClipboard(record.value, 'value-' + index)}
                          >
                            <CopyIcon />
                          </Button>
                        }
                      />
                    </Box>
                    
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {record.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Current DNS Records */}
          {(verificationData as any)?.current_dns_records && (
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 2 }}>
                  Current DNS Records
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries((verificationData as any).current_dns_records).map(([type, records]) => (
                    <Chip
                      key={type}
                      size="sm"
                      variant="outlined"
                      startDecorator={<DnsIcon />}
                    >
                      {type}: {Array.isArray(records) ? records.length : 0}
                    </Chip>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Help Links */}
          <Card variant="soft" color="primary">
            <CardContent>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Need Help?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  size="sm"
                  variant="plain"
                  startDecorator={<OpenIcon />}
                  onClick={() => window.open('https://help.apexos.com/dns-setup', '_blank')}
                >
                  DNS Setup Guide
                </Button>
                <Button
                  size="sm"
                  variant="plain"
                  startDecorator={<OpenIcon />}
                  onClick={() => window.open('https://help.apexos.com/contact', '_blank')}
                >
                  Contact Support
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={onClose}>
              Close
            </Button>
          </Box>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
